import React from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

interface SEOProps {
  title?: string;
  description?: string;
  keywords?: string;
  image?: string;
  noindex?: boolean;
}

export function SEO({ 
  title, 
  description, 
  keywords,
  image = '/og-image.png',
  noindex = false 
}: SEOProps) {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  
  // Get current language
  const currentLang = i18n.language;
  const isLT = currentLang === 'lt';
  
  // Default values based on language
  const defaults = {
    en: {
      title: 'DiskRay - Simple Cloud Storage That Just Works',
      description: 'Secure, affordable cloud storage with easy setup. Access your files from anywhere. No technical skills required.',
      keywords: 'cloud storage, secure storage, file sharing, remote access, online storage, cloud backup, data storage, storage solution'
    },
    lt: {
      title: 'DiskRay - Paprasta debesų saugykla, kuri tiesiog veikia',
      description: 'Saugi, prieinama debesų saugykla su paprastu įdiegimu. Pasiekite savo failus iš bet kur. Nereikia techninių žinių.',
      keywords: 'debesų saugykla, saugi saugykla, failų dalijimasis, nuotolinis priėjimas, internetinė saugykla, debesų atsarginės kopijos, duomenų saugykla'
    }
  };
  
  const lang = isLT ? 'lt' : 'en';
  const siteTitle = title ? `${title} | DiskRay` : defaults[lang].title;
  const siteDescription = description || defaults[lang].description;
  const siteKeywords = keywords || defaults[lang].keywords;
  
  // Generate alternate language URLs
  const baseUrl = 'https://diskray.com';
  const currentPath = location.pathname;
  const canonicalUrl = `${baseUrl}${currentPath}`;
  
  return (
    <>
      <title>{siteTitle}</title>
      <meta name="description" content={siteDescription} />
      <meta name="keywords" content={siteKeywords} />
      
      {/* Canonical URL */}
      <link rel="canonical" href={canonicalUrl} />
      
      {/* Language alternates */}
      <link rel="alternate" hrefLang="en" href={`${baseUrl}/en${currentPath}`} />
      <link rel="alternate" hrefLang="lt" href={`${baseUrl}/lt${currentPath}`} />
      <link rel="alternate" hrefLang="x-default" href={`${baseUrl}${currentPath}`} />
      
      {/* Open Graph */}
      <meta property="og:title" content={siteTitle} />
      <meta property="og:description" content={siteDescription} />
      <meta property="og:type" content="website" />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:image" content={`${baseUrl}${image}`} />
      <meta property="og:site_name" content="DiskRay" />
      <meta property="og:locale" content={currentLang === 'lt' ? 'lt_LT' : 'en_US'} />
      
      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={siteTitle} />
      <meta name="twitter:description" content={siteDescription} />
      <meta name="twitter:image" content={`${baseUrl}${image}`} />
      
      {/* Additional SEO tags */}
      <meta name="robots" content={noindex ? 'noindex,nofollow' : 'index,follow'} />
      <meta name="googlebot" content={noindex ? 'noindex,nofollow' : 'index,follow'} />
      
      {/* Structured Data for LocalBusiness */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "SoftwareApplication",
          "name": "DiskRay",
          "applicationCategory": "Storage",
          "operatingSystem": "Web",
          "offers": {
            "@type": "Offer",
            "price": "4.99",
            "priceCurrency": "EUR",
            "priceSpecification": {
              "@type": "PriceSpecification",
              "price": "4.99",
              "priceCurrency": "EUR",
              "unitText": "MONTH"
            }
          },
          "description": siteDescription,
          "url": baseUrl,
          "featureList": [
            isLT ? "Lengvas failų dalijimasis" : "Easy file sharing",
            isLT ? "Saugus nuotolinis priėjimas" : "Secure remote access", 
            isLT ? "Automatinės atsarginės kopijos" : "Automatic backups",
            isLT ? "Nereikia techninių žinių" : "No technical knowledge required"
          ],
          "screenshot": `${baseUrl}/screenshots/dashboard.png`,
          "aggregateRating": {
            "@type": "AggregateRating",
            "ratingValue": "4.8",
            "reviewCount": "127"
          }
        })}
      </script>
    </>
  );
}